import { FC, useCallback, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, MenuItem, styled } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp, faSquareList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, IValidationError, Markdown, Multiselect, Select, Text } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationForm } from "@/utilities/forms/ConfigurationFormProcessor";
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, ConfigurationTitle, OtherVersionsBox, OtherVersionsItem } from "./ConfigurationFormEditInfo";
import { Condition, ConditionMode, IFormState, useFormActions } from "@ngt/forms-core";
import { IEventMetadata, IFormMetadata, ISourceDataConfigurationForm, NoteStatus, NoteType, SourceDataPriority } from '@/api/dtos';
import { get } from "lodash-es";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import { faExclamationTriangle, faTriangle } from "@fortawesome/pro-solid-svg-icons";

export interface IConfigurationFormEditFormProps {
    context: IConfigurationFormEditContext<IConfigurationForm>
}

const onConditionNotMet = () => {};

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey: string;
    formMetadata: IFormMetadata;
    formConfiguration: ISourceDataConfigurationForm;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

const ConfigurationFormEditForm: FC<IConfigurationFormEditFormProps> = ({
    context
}) => {
    const {
        configurationPropertyPath
    } = context;
    
    const [openOtherVersions, setOpenOtherVersions] = useState(false);

    const actions = useFormActions();

    const allVersions = useMemo(() => {
        if (context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const events = context.item?.formEvents?.[context.item.formKey!] ?? [];

        const versions: IOtherVersion[] = [];

        for (let event of events) {

            const localPropertyPath = `configuration.events.${event.eventKey!}.forms.${context.item.formKey!}`;
            const localFormVersion = actions.getFieldValue<ISourceDataConfigurationForm>(localPropertyPath);
            
            if (localFormVersion /*&& !globalFormVersion?.linked*/) {
                const data = {
                    type: ConfigurationType.Form,
                    eventKey: event.eventKey!,
                    eventMetadata: event.eventMetadata,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formConfiguration: localFormVersion,
                    configurationPropertyPath: localPropertyPath,
                    linked: !!localFormVersion?.linked,
                    isGlobal: false,
                    isTrial: false
                }
    
                if (context.configurationPropertyPath !== localPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }

        }

        const globalPropertyPath = `configuration.forms.${context.item.formKey!}`;
        const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationForm>(globalPropertyPath);
        
        if (globalFormVersion /*&& !globalFormVersion?.linked*/) {
            const data = {
                type: ConfigurationType.Form,
                eventKey: undefined,
                eventMetadata: undefined,
                formKey: context.item.formKey!,
                formMetadata: context.item.formMetadata!,
                formConfiguration: globalFormVersion,
                configurationPropertyPath: globalPropertyPath,
                linked: !!globalFormVersion?.linked,
                isGlobal: !context.item.isTrialConfiguration,
                isTrial: !!context.item.isTrialConfiguration
            }

            if (context.configurationPropertyPath !== globalPropertyPath) {
                versions.push(data)
            }
            else {
                currentVersion = data;
            }
        }

        const trialPropertyPath = `trialConfiguration.forms.${context.item.formKey!}`
        const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationForm>(trialPropertyPath);
        
        if (trialFormVersion /*&& !trialFormVersion?.linked*/) {
            const data = {
                type: ConfigurationType.Form,
                eventKey: undefined,
                eventMetadata: undefined,
                formKey: context.item.formKey!,
                formMetadata: context.item.formMetadata!,
                formConfiguration: trialFormVersion,
                configurationPropertyPath: trialPropertyPath,
                linked: !!trialFormVersion?.linked,
                isGlobal: false,
                isTrial: true
            }

            if (context.configurationPropertyPath !== trialPropertyPath) {
                versions.push(data)
            }
            else {
                currentVersion = data;
            }
        }


        

        return {
            otherVersions: versions.sort((a, b) => {
                
                if (!a.isTrial && b.isTrial) {
                    return -1;
                }
                else if (a.isTrial && !b.isTrial) {
                    return 1;
                }

                if (!a.isGlobal && b.isGlobal) {
                    return -1;
                }
                else if (a.isGlobal && !b.isGlobal) {
                    return 1;
                }


                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formKey !== b.formKey) {
                    return a.formKey < b.formKey ? -1 : 1;
                }
                
                return -1;
            }),
            currentVersion
        };
    }, [context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);

    const showNoteCondition = useCallback((state: IFormState<any, IValidationError>, parentPropertyPath?: string | null) => {
        const showNotes = get(state.value, `${configurationPropertyPath}.showNotes`);

        return !!showNotes;
    }, [configurationPropertyPath]);
    
    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type="Form"
            typeIcon={faSquareList}
        >
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            Other Versions
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
                <InputOnlyField 
                    label="Hide Form When No Data Provided"
                    propertyPath={`${configurationPropertyPath}.excludeBlank`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>
            
            <ConfigurationTitle variant="h4">
                Discrepancy Notes
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Show Discrepancy Notes"
                    propertyPath={`${configurationPropertyPath}.showNotes`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
                <Condition 
                    mode={context.usingTrialConfiguration ? ConditionMode.Enable : ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={showNoteCondition}
                    onConditionNotMet={onConditionNotMet}
                >
                    <InputOnlyField 
                        label="Types"
                        propertyPath={`${configurationPropertyPath}.noteTypes`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteType.ValidationFailed}
                        >
                            Validation Failed
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Annotation}
                        >
                            Annotation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Query}
                        >
                            Query
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ReasonForChange}
                        >
                            Reason For Change
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Internal}
                        >
                            Internal
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ProtocolDeviation}
                        >
                            Protocol Deviation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ImportantProtocolDeviation}
                        >
                            Important Protocol Deviation
                        </MenuItem>
                    </InputOnlyField>
                    <InputOnlyField 
                        label="Statuses"
                        propertyPath={`${configurationPropertyPath}.noteStatuses`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteStatus.New}
                        >
                            New
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Updated}
                        >
                            Updated
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.ResolutionProposed}
                        >
                            Resolution Proposed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Closed}
                        >
                            Closed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.NotApplicable}
                        >
                            Not Applicable
                        </MenuItem>
                    </InputOnlyField>
                </Condition>
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditForm;